// src/components/LoginForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, UserPlus, Eye, EyeOff, HelpCircle, Sparkles } from 'lucide-react';
import ForgotPasswordForm from './ForgotPasswordForm';
import { track, Events } from '../utils/analytics';
import { trackEvent } from '../services/analyticsService';
import { generateUsername } from '../utils/usernameGenerator';

interface LoginFormProps {
  initialMode?: 'login' | 'register';
  onModeChange?: (mode: 'login' | 'register') => void;
  onSignupClick?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ initialMode = 'login', onModeChange, onSignupClick }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithMagicLink, isAuthenticated, hasHydrated, sessionChecked } = useAuth();

  const [isLogin, setIsLogin] = useState(initialMode === 'login');

  // Form fields
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');

  // Username availability UI state
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'ok' | 'taken' | 'invalid'>('idle');
  const [usernameMessage, setUsernameMessage] = useState<string>('');

  // Password fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const passwordsMatch = confirmPassword.length === 0 ? true : password === confirmPassword;

  // Visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI flags
  const [error, setError] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Forgot flow
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState<'password' | 'username'>('password');

  // Magic link
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Username availability check (debounced)
  useEffect(() => {
    if (!username) {
      setUsernameStatus('idle');
      setUsernameMessage('');
      return;
    }

    const candidate = username.trim();

    if (!/^[a-zA-Z0-9_]{3,20}$/.test(candidate)) {
      setUsernameStatus('invalid');
      setUsernameMessage('');
      return;
    }

    let cancelled = false;
    setUsernameStatus('checking');
    setUsernameMessage('');

    const t = setTimeout(async () => {
      try {
        const base = import.meta.env.VITE_SUPABASE_URL!;
        const anon = import.meta.env.VITE_SUPABASE_ANON_KEY!;
        const res = await fetch(`${base}/functions/v1/check-username-availability`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anon}`,
          },
          body: JSON.stringify({ username: candidate })
        });

        if (cancelled) return;

        if (!res.ok) {
          console.error('[username-check] HTTP', res.status);
          setUsernameStatus('idle');
          setUsernameMessage('Could not verify right now. Try again.');
          return;
        }

        const json = await res.json();

        if (json?.available === true) {
          setUsernameStatus('ok');
        } else if (json?.available === false && json?.reason === 'invalid') {
          setUsernameStatus('invalid');
        } else if (json?.available === false) {
          setUsernameStatus('taken');
        } else {
          setUsernameStatus('idle');
          setUsernameMessage('Could not verify right now. Try again.');
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[username-check] network error', e);
          setUsernameStatus('idle');
          setUsernameMessage('Network issue checking username.');
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [username]);

  // If already authenticated, go straight in
  useEffect(() => {
    if (isAuthenticated && hasHydrated && sessionChecked) {
      const returnUrl = searchParams.get('returnUrl') || '/vybes';
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, hasHydrated, sessionChecked, navigate, searchParams]);

  // Keep external mode toggle in sync if provided
  useEffect(() => {
    setIsLogin(initialMode === 'login');
  }, [initialMode]);

  const validateEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setError('');

    // Magic link flow
    if (isLogin && useMagicLink) {
      if (!email) {
        setError('Please enter your email');
        return;
      }
      if (!validateEmail(email)) {
        setError('Please enter a valid email address');
        return;
      }

      setIsLoading(true);
      try {
        // Check if email exists first
        const base = import.meta.env.VITE_SUPABASE_URL!;
        const anon = import.meta.env.VITE_SUPABASE_ANON_KEY!;
        const checkRes = await fetch(`${base}/functions/v1/check-email-exists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anon}`,
          },
          body: JSON.stringify({ email: email.trim() })
        });

        if (!checkRes.ok) {
          setError('Unable to verify email address. Please try again.');
          setIsLoading(false);
          return;
        }

        const checkData = await checkRes.json();

        if (!checkData.exists) {
          setError('Email address not recognized. Please check your email or sign up for a new account.');
          setIsLoading(false);
          return;
        }

        // Email exists, proceed with magic link
        const result = await loginWithMagicLink(email);
        if (result.success) {
          setMagicLinkSent(true);
        } else {
          setError(result.message);
        }
      } catch (err) {
        console.error('Magic link error:', err);
        setError('Failed to send magic link');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Extra sign-up checks
    if (!isLogin) {
      // ALWAYS use ProfileBuilderNew for signup via onSignupClick callback
      if (!onSignupClick) {
        console.error('LoginForm: onSignupClick callback is required for registration');
        setFormError('Sign up is not configured. Please refresh the page.');
        return;
      }

      // Trigger ProfileBuilderNew
      onSignupClick();
      return;
    }

    // Basic validation
    if (!email || !password) {
      setError('Please complete all required fields');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      // LOGIN FLOW ONLY (register is handled by ProfileBuilderNew)
      trackEvent('login_attempt', { email });
      const ok = await login({ email, password });
      if (!ok) throw new Error('Login failed. Please check your credentials.');
      trackEvent('login_success', { email });
      const returnUrl = searchParams.get('returnUrl') || '/vybes';
      navigate(returnUrl, { replace: true });
    } catch (err) {
      console.error('Auth error:', err);
      trackEvent('login_failed', {
        email,
        error: err instanceof Error ? err.message : 'Unknown error'
      });
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    // If switching to signup and callback provided, use that
    if (isLogin && onSignupClick) {
      onSignupClick();
      return;
    }

    const next = !isLogin;
    setIsLogin(next);
    setError('');
    setUsername('');
    setConfirmPassword('');
    setFormError(null);
    onModeChange?.(next ? 'login' : 'register');
  };

  if (showForgotPassword) {
    return (
      <ForgotPasswordForm
        onBack={() => setShowForgotPassword(false)}
        mode={forgotMode}
      />
    );
  }

  // Magic link sent success
  if (magicLinkSent) {
    return (
      <div className="bg-primary-50 p-6 rounded-lg max-w-md mx-auto">
        <div className="text-center">
          <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent2">
              <rect width="20" height="16" x="2" y="4" rx="2"></rect>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-contrast mb-4">Check Your Email</h2>
          <p className="text-contrast-400 mb-6">
            We've sent a magic link to <strong>{email}</strong>. Click the link in your email to log in.
          </p>
          <button
            onClick={() => {
              setMagicLinkSent(false);
              setEmail('');
            }}
            className="text-secondary hover:text-secondary-600 text-sm font-medium"
          >
            Try a different email
          </button>
        </div>
      </div>
    );
  }

  // We keep the success card as a fallback, but we navigate immediately now.
  if (success) {
    return (
      <div className="bg-primary-50 p-6 rounded-lg max-w-md mx-auto">
        <div className="text-center">
          <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent2">
              <path d="M20 6L9 17l-5-5"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-contrast mb-4">
            {isLogin ? 'Login Successful!' : 'Registration Successful!'}
          </h2>
          <p className="text-contrast-400 mb-6">
            {isLogin
              ? 'You have been successfully logged in.'
              : 'Your account has been created. Please check your email to verify your account.'}
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-secondary mx-auto"></div>
          <p className="text-sm text-contrast-500 mt-4">Redirecting you to your Vybes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-theme-cards p-4 sm:p-6 rounded-2xl shadow-card border border-theme-cards w-full max-w-md">
      <h2 className="text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6 text-contrast">
        {isLogin ? 'Welcome Back' : 'Create Account'}
      </h2>

      <p className="text-contrast-400 mb-4 sm:mb-6 text-center text-sm sm:text-base">
        {isLogin ? 'Enter your credentials to access your account' : 'Sign up to get started'}
      </p>

      {formError && (
        <div className="bg-secondary/10 text-secondary p-2 sm:3 rounded-md mb-3 sm:mb-4 text-sm">
          {formError}
        </div>
      )}

      {error && (
        <div className="bg-secondary/10 text-secondary p-2 sm:3 rounded-md mb-3 sm:mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Email */}
        <div className="mb-3 sm:mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-contrast mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full p-3 text-base bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast ${
              error.toLowerCase().includes('email') ? 'border-secondary' : 'border-primary-200'
            }`}
            placeholder="Enter your email"
            disabled={isLoading}
          />
        </div>

        {/* Username (register only) */}
        {!isLogin && (
          <div className="mb-3 sm:mb-4">
            <label className="block text-sm font-medium text-contrast mb-1">
              Username
            </label>
            <div className="relative">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                className="w-full bg-primary-100 border border-primary-200 rounded-md px-3 py-2 pr-28 text-contrast placeholder-contrast-500 focus:ring-2 focus:ring-secondary focus:border-secondary"
              />
              <button
                type="button"
                onClick={() => {
                  const generated = generateUsername();
                  setUsername(generated);
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs rounded-md hover:from-red-600 hover:to-pink-700 transition-colors flex items-center gap-1 font-medium"
                disabled={isLoading}
              >
                <Sparkles size={14} />
                Generate
              </button>
            </div>
            <div className="mt-1 text-xs">
              {usernameStatus === 'checking' && <span className="text-contrast-500">Checking availability…</span>}
              {usernameStatus === 'ok' && <span className="text-green-500">Great — that username is available!</span>}
              {usernameStatus === 'taken' && <span className="text-red-500">Sorry, that username is taken.</span>}
              {usernameStatus === 'invalid' && (
                <span className="text-red-500">
                  Use 3–20 characters: letters, numbers, or underscores only.
                </span>
              )}
              {usernameMessage && <span className="text-contrast-500">{usernameMessage}</span>}
            </div>
          </div>
        )}

        {/* Magic Link Toggle (login only) */}
        {isLogin && (
          <div className="mb-4 flex items-center justify-center">
            <button
              type="button"
              onClick={() => {
                setUseMagicLink(!useMagicLink);
                setError('');
              }}
              className="text-sm text-green-500 hover:text-green-600 font-medium"
            >
              {useMagicLink ? 'Use password instead' : 'Use magic link instead'}
            </button>
          </div>
        )}

        {/* Password (only show if not using magic link) */}
        {!useMagicLink && (
          <div className="mb-3 sm:mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-contrast mb-1">
              Password
            </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setFormError(null); }}
              className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast pr-10 ${
                error.toLowerCase().includes('password') ? 'border-secondary' : 'border-primary-200'
              }`}
              placeholder="Enter your password"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-contrast-500 hover:text-contrast min-h-[44px] min-w-[44px] justify-center"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {!isLogin && (
            <p className="text-xs text-contrast-500 mt-1">Password must be at least 8 characters</p>
          )}
          </div>
        )}

        {/* Confirm Password — register only */}
        {!isLogin && (
          <div className="mb-4 sm:mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-contrast mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); setFormError(null); }}
                className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast pr-10 ${
                  error.toLowerCase().includes('match') ? 'border-secondary' : 'border-primary-200'
                }`}
                placeholder="Re-enter your password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((s) => !s)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-contrast-500 hover:text-contrast min-h-[44px] min-w-[44px] justify-center"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {confirmPassword.length > 0 && password.length > 0 && (
              <p className={`mt-1 text-xs ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}>
                {passwordsMatch ? 'Passwords match' : "Passwords don't match"}
              </p>
            )}
          </div>
        )}

        {/* Forgot links (login only) */}
        {isLogin && (
          <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4 sm:mb-6">
            <button
              type="button"
              onClick={() => { setForgotMode('password'); setShowForgotPassword(true); }}
              className="text-secondary hover:text-secondary-600 text-sm min-h-[44px] flex items-center"
            >
              Forgot password?
            </button>
            <button
              type="button"
              onClick={() => { setForgotMode('username'); setShowForgotPassword(true); }}
              className="text-secondary hover:text-secondary-600 text-sm min-h-[44px] flex items-center"
            >
              Forgot username?
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={
            !isLogin
              ? (!password || !confirmPassword || !passwordsMatch || isLoading || usernameStatus === 'checking' || usernameStatus === 'taken' || usernameStatus === 'invalid')
              : (useMagicLink ? (!email || isLoading) : isLoading)
          }
          className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white py-2 sm:py-3 rounded-full hover:from-red-600 hover:to-pink-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] font-semibold"
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            <>
              {isLogin ? (
                <>
                  <LogIn size={18} className="mr-2" />
                  {useMagicLink ? 'Send Magic Link' : 'Login'}
                </>
              ) : (
                <>
                  <UserPlus size={18} className="mr-2" />
                  Sign Up
                </>
              )}
            </>
          )}
        </button>
      </form>

      <div className="mt-3 sm:mt-4 text-center">
        <button
          onClick={toggleMode}
          className="text-green-500 hover:text-green-600 text-xs sm:text-sm font-medium min-h-[36px]"
        >
          {isLogin ? "Don't have an account? Sign up" : "Already have an account? Login"}
        </button>
      </div>

      {isLogin && (
        <div className="mt-4 sm:mt-6 text-center">
          <div className="flex items-center justify-center text-xs sm:text-sm text-contrast-500">
            <HelpCircle size={14} className="mr-1" />
            <span>Need help? Contact support</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
